// src/rms/agreements/library/index.js
// ============================================================================
// The starter documents, assembled
// ============================================================================
// NOT LEGAL ADVICE. Read the header of common.js before touching anything here.
//
// This module turns the spine plus a trade's deltas into the plain text that
// gets copied into rms_agreement_templates the first time an operator opens
// their Agreements tab. After that copy the operator owns the text and nothing
// here is read again for them -- the same arrangement seed.js has with
// operations.addons, and for the same reason: two car rental businesses start
// identical and diverge the moment one of them edits.
//
// ----------------------------------------------------------------------------
// LIBRARY_VERSION
// ----------------------------------------------------------------------------
// Stamped onto each row at copy time. It exists so an operator can later be
// told "the standard tool rental agreement has been revised since yours was
// created" without anything being changed under them. Bump it when the PROSE
// changes in a way an existing operator would want to know about. Do not bump
// it for a typo, and never use it to trigger an automatic rewrite: the text an
// operator has edited is theirs.
// ============================================================================

import { SPINE, SHARED, VERTICAL_SLOT } from "./common";
import { car_rental, rv_rental, boat_rental, atv_rental, bicycle_rental } from "./vehicles";
import { tool_rental, construction_equipment, party_equipment, photo_av_gear } from "./equipment";
import { costume_formalwear, camping_gear, storage_unit } from "./goods";

export const LIBRARY_VERSION = 1;

/** The languages the starter bodies are written in. Everything else falls back
 *  to English -- see documentsFor. */
export const LIBRARY_LOCALES = ["en", "fr"];

const TRADES = {
  car_rental,
  boat_rental,
  atv_rental,
  bicycle_rental,
  storage_unit,
  tool_rental,
  party_equipment,
  photo_av_gear,
  costume_formalwear,
  camping_gear,
  rv_rental,
  construction_equipment,
};

/** Every vertical the library covers. The verifier checks this against the
 *  presets, so a thirteenth trade fails loudly rather than silently shipping
 *  with no contract. */
export const COVERED_VERTICALS = Object.keys(TRADES);

// ----------------------------------------------------------------------------

/** Pick a language out of a { en, fr } pair. */
const pick = (pair, locale) => {
  if (!pair) return "";
  // Match on the base language: a customer whose locale is "fr-CA" gets the
  // French body, not the English one. Region is not modelled here, and a
  // Canadian and a French operator both start from the same text and edit it.
  const base = String(locale || "en").toLowerCase().split(/[-_]/)[0];
  return pair[base] || pair.en || "";
};

/**
 * Clause objects into one plain-text body.
 *
 * Numbering happens here rather than in the prose, so inserting a trade clause
 * in the middle of the spine does not leave an operator with two clause 7s.
 * Once the text is copied to their row it is ordinary text and the numbers are
 * theirs to maintain -- which is the right trade: a stored body is exactly what
 * gets signed, with nothing computed at render time.
 */
const toBody = (clauses, locale) =>
  clauses
    .map((clause, i) => `## ${i + 1}. ${pick(clause.heading, locale)}\n\n${pick(clause.body, locale)}`)
    .join("\n\n");

/** The spine, with a trade's overrides applied and its own clauses spliced in. */
const assembleAgreement = (trade) => {
  const out = [];
  for (const key of SPINE) {
    if (key === VERTICAL_SLOT) {
      out.push(...(trade.clauses || []));
      continue;
    }
    const clause = (trade.overrides && trade.overrides[key]) || SHARED[key];
    if (clause) out.push(clause);
  }
  return out;
};

/**
 * Every starter document for one trade, in one language.
 *
 * @returns {Array<{docKey, kind, title, body}>} ready to write as template rows
 */
export function documentsFor(verticalKey, locale = "en") {
  const trade = TRADES[verticalKey];
  if (!trade) return [];

  const agreement = {
    docKey: "rental_agreement",
    kind: "agreement",
    title: pick(trade.title, locale),
    body: toBody(assembleAgreement(trade), locale),
  };

  const extras = (trade.extras || []).map((doc) => ({
    docKey: doc.docKey,
    kind: doc.kind,
    title: pick(doc.title, locale),
    body: toBody(doc.clauses, locale),
  }));

  return [agreement, ...extras];
}

/**
 * Which documents a trade gets, without building any prose.
 * Cheap enough for a checklist or an empty state.
 */
export function docKeysFor(verticalKey) {
  const trade = TRADES[verticalKey];
  if (!trade) return [];
  return ["rental_agreement", ...(trade.extras || []).map((d) => d.docKey)];
}

/** Every (vertical, locale) pair the library can produce. For the verifier. */
export function everyDocument() {
  const rows = [];
  for (const key of COVERED_VERTICALS) {
    for (const locale of LIBRARY_LOCALES) {
      for (const doc of documentsFor(key, locale)) {
        rows.push({ verticalKey: key, locale, ...doc });
      }
    }
  }
  return rows;
}

export default {
  LIBRARY_VERSION,
  LIBRARY_LOCALES,
  COVERED_VERTICALS,
  documentsFor,
  docKeysFor,
  everyDocument,
};
